import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getCardHref, loadStoredBrandConfig } from "../config/brand.js";
import { useAuth } from "../context/AuthContext.jsx";

const NAV_ITEMS = [
  { to: "/pricing", label: "Pricing" },
  { to: "/markets", label: "Markets" },
  { to: "/contact", label: "Contact" },
];

function MenuIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export default function SiteHeader() {
  const brand = loadStoredBrandConfig();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, ready, logout, isAuthenticated } = useAuth();
  const onLoginPage = pathname === "/login";
  const isHome = pathname === "/";
  const serviceCards = brand.featureCards.filter((card) => card.slug);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.classList.add("site-nav-open");
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("site-nav-open");
    };
  }, [open]);

  return (
    <header className="site-header">
      <div className="site-header__bar">
        <Link className="site-header__logo" to="/" aria-label={brand.identity.name}>
          <img
            className="site-header__logo-img"
            src={brand.identity.logoSrc}
            alt={brand.identity.logoAlt}
            width={400}
            height={80}
            decoding="async"
          />
        </Link>

        {isHome ? (
          <div
            className="site-header__hero-cards-slot"
            id="site-header-hero-cards"
          />
        ) : null}

        <nav className="site-header__nav-desktop" aria-label="Primary">
          <div className="site-header__services-menu">
            <button
              type="button"
              className="site-header__link site-header__services-trigger"
              aria-haspopup="true"
            >
              Services
            </button>
            <div className="site-header__services-dropdown" role="menu">
              {serviceCards.map((card, index) => {
                const to = getCardHref(card);
                const key = card.slug ?? card.ctaPath ?? `service-${index}`;
                return (
                  <Link
                    key={key}
                    className="site-header__services-link"
                    to={to}
                    role="menuitem"
                  >
                    <span className="site-header__services-stat" aria-hidden="true">
                      {card.stat}
                    </span>
                    <span className="site-header__services-text">
                      <span className="site-header__services-kicker">
                        {card.kicker}
                      </span>
                      <span className="site-header__services-title">
                        {card.title}
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
          {NAV_ITEMS.map(({ to, label }) => (
            <Link key={to} className="site-header__link" to={to}>
              {label}
            </Link>
          ))}
          {!onLoginPage && ready && isAuthenticated ? (
            <>
              <span className="site-header__account" title={user?.email}>
                {user?.email}
              </span>
              <button
                type="button"
                className="site-header__link site-header__link--button"
                onClick={() => {
                  logout();
                }}
              >
                Sign out
              </button>
            </>
          ) : null}
          {!onLoginPage && ready && !isAuthenticated ? (
            <Link className="site-header__link" to="/login">
              Sign in
            </Link>
          ) : null}
        </nav>

        <button
          type="button"
          className="site-header__menu-btn"
          aria-expanded={open}
          aria-controls="site-nav-panel"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>

      <div
        className={`site-header__backdrop ${open ? "is-visible" : ""}`}
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />

      <aside
        id="site-nav-panel"
        className={`site-header__panel ${open ? "is-open" : ""}`}
        aria-hidden={!open}
      >
        <div className="site-header__panel-head">
          <span className="site-header__panel-title">Menu</span>
          <button
            type="button"
            className="site-header__panel-close"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="site-header__panel-nav" aria-label="Mobile primary">
          {NAV_ITEMS.map(({ to, label }) => (
            <Link
              key={to}
              className="site-header__panel-link"
              to={to}
              onClick={() => setOpen(false)}
            >
              {label}
            </Link>
          ))}
          <p className="site-header__panel-services-label">Services</p>
          {serviceCards.map((card, index) => {
            const to = getCardHref(card);
            const key = card.slug ?? card.ctaPath ?? `card-${index}`;
            return (
              <Link
                key={key}
                className="site-header__panel-link site-header__panel-link--service"
                to={to}
                onClick={() => setOpen(false)}
              >
                <span
                  className="site-header__panel-service-stat"
                  aria-hidden="true"
                >
                  {card.stat}
                </span>
                <span className="site-header__panel-service-text">
                  <span className="site-header__panel-service-kicker">
                    {card.kicker}
                  </span>
                  <span className="site-header__panel-service-title">
                    {card.title}
                  </span>
                </span>
              </Link>
            );
          })}
          {!onLoginPage && ready && isAuthenticated ? (
            <>
              <span className="site-header__panel-account">{user?.email}</span>
              <button
                type="button"
                className="site-header__panel-link site-header__panel-link--button"
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
              >
                Sign out
              </button>
            </>
          ) : null}
          {!onLoginPage && ready && !isAuthenticated ? (
            <Link
              className="site-header__panel-link"
              to="/login"
              onClick={() => setOpen(false)}
            >
              Sign in
            </Link>
          ) : null}
        </nav>
      </aside>
    </header>
  );
}
